<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useSidebarContext } from './provider';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';
import {
  ROSH_ITEM_BASE,
  ROSH_ITEM_ACTIVE,
  roshItemSize,
} from './roshItemClasses';
import { roshChannelIconClass, roshChannelKind } from './roshChannel';

// Rosh whitelabel: os canais (inboxes) ficam numa seção própria no topo da
// sidebar — o ponto de entrada do atendimento — em vez de aninhados em
// Conversas › Channels.
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { isCollapsed } = useSidebarContext();

const inboxes = useMapGetter('inboxes/getInboxes');
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);

// Agrupa por tipo de canal (na ordem do mockup) e, dentro de cada grupo,
// ordena alfabeticamente.
const KIND_ORDER = ['whatsapp', 'email', 'web', 'other'];

const sortedInboxes = computed(() =>
  [...inboxes.value].sort((a, b) => {
    const byKind =
      KIND_ORDER.indexOf(roshChannelKind(a)) -
      KIND_ORDER.indexOf(roshChannelKind(b));
    if (byKind !== 0) return byKind;
    return (a.name || '').localeCompare(b.name || '');
  })
);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="inboxes.length"
    class="flex flex-col gap-1 min-w-0"
    :class="isCollapsed ? 'items-center' : ''"
  >
    <span
      v-if="!isCollapsed"
      class="px-2 text-[11px] font-semibold tracking-wider uppercase truncate text-n-slate-10"
    >
      {{ t('SIDEBAR.CHANNELS') }}
    </span>
    <RouterLink
      v-for="inbox in sortedInboxes"
      :key="inbox.id"
      :to="accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id })"
      :title="inbox.name"
      :active-class="ROSH_ITEM_ACTIVE"
      :class="[ROSH_ITEM_BASE, roshItemSize(isCollapsed)]"
    >
      <ChannelIcon
        :inbox="inbox"
        class="flex-shrink-0 size-4"
        :class="roshChannelIconClass(inbox)"
      />
      <span v-if="!isCollapsed" class="flex-1 text-sm truncate text-start">
        {{ inbox.name }}
      </span>
      <SidebarUnreadBadge
        v-if="!isCollapsed"
        :count="getInboxUnreadCount(inbox.id)"
        variant="unread"
      />
    </RouterLink>
  </div>
</template>
